import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { getJSON, patchJSON } from '@/lib/api'
import type { MembershipLevel, MembershipLevelsResponse } from './MembershipLevelsPage.types'
import { AdminDirectoryFieldsPanel } from './AdminDirectoryFieldsPanel'

type Settings = { member_directory_settings: { enabled: boolean; public_enabled: boolean } }

export function AdminDirectorySettingsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const settingsQuery = useQuery({
    queryKey: ['member-directory-settings'],
    queryFn: () => getJSON<Settings>('/api/v1/member_directory_settings'),
    retry: false,
  })
  const levelsQuery = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => getJSON<MembershipLevelsResponse>('/api/v1/membership_levels'),
    retry: false,
  })
  const updateSettings = useMutation({
    mutationFn: (changes: { enabled?: boolean; public_enabled?: boolean }) =>
      patchJSON<Settings>('/api/v1/member_directory_settings', {
        member_directory_settings: changes,
      }),
    onSuccess: (data) => queryClient.setQueryData(['member-directory-settings'], data),
  })
  const updateLevel = useMutation({
    mutationFn: ({ id, included }: { id: number; included: boolean }) =>
      patchJSON<{ membership_level: MembershipLevel }>(`/api/v1/membership_levels/${id}`, {
        membership_level: { include_in_member_directory: included },
      }),
    onSuccess: (data) =>
      queryClient.setQueryData<MembershipLevelsResponse>(['membership-levels'], (current) =>
        current
          ? {
              membership_levels: current.membership_levels.map((level) =>
                level.id === data.membership_level.id ? data.membership_level : level,
              ),
            }
          : current,
      ),
  })
  if (settingsQuery.isLoading || levelsQuery.isLoading)
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading directory settings...
      </p>
    )
  if (settingsQuery.isError || levelsQuery.isError)
    return <Alert>Directory settings unavailable</Alert>
  const settings = settingsQuery.data?.member_directory_settings
  const levels = levelsQuery.data?.membership_levels ?? []
  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 px-6 py-6">
      <header className="space-y-1">
        <h1 className="text-heading-sm font-semibold text-foreground">Member directory</h1>
        <p className="text-body-sm text-muted-foreground">
          Control who appears in the member directory and what each field shows.
        </p>
      </header>
      <section id="renderings" className="overflow-hidden rounded-md border border-border bg-card">
        <h2 className="border-b border-border px-4 py-3 text-body font-medium text-foreground">
          Renderings
        </h2>
        <DirectorySwitch
          label="Member portal directory"
          description="Members can browse each other inside the portal."
          checked={settings?.enabled ?? false}
          onCheckedChange={(enabled) => updateSettings.mutate({ enabled })}
        />
        <div className="border-t border-border p-4">
          <DirectorySwitch
            label="Public website directory"
            description="Anyone can browse a members-only subset without signing in."
            checked={settings?.public_enabled ?? false}
            onCheckedChange={(public_enabled) => updateSettings.mutate({ public_enabled })}
          />
          {settings?.public_enabled && (
            <p className="pt-2 text-caption text-muted-foreground">
              {window.location.host}/directory
            </p>
          )}
        </div>
      </section>
      <AdminDirectoryFieldsPanel />
      <section
        id="membership-levels"
        className="overflow-hidden rounded-md border border-border bg-card"
      >
        <h2 className="border-b border-border px-4 py-3 text-body font-medium text-foreground">
          Membership levels
        </h2>
        {levels.map((level, index) => (
          <div
            key={level.id}
            className={`flex items-center justify-between gap-4 px-4 py-2.5 ${index > 0 ? 'border-t border-border' : ''}`}
          >
            <p className="text-body-sm text-foreground">{level.name}</p>
            <Switch
              checked={level.include_in_member_directory ?? true}
              aria-label={`Include ${level.name} in the directory`}
              onCheckedChange={(included) => updateLevel.mutate({ id: level.id, included })}
            />
          </div>
        ))}
      </section>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => navigate('/admin/directory/preview')}
      >
        Preview the member directory
      </Button>
    </div>
  )
}

function DirectorySwitch({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string
  description: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-4 p-4">
      <div className="space-y-0.5">
        <p className="text-body-sm text-foreground">{label}</p>
        <p className="text-caption text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={label} />
    </div>
  )
}

export function AdminDirectoryPreviewPage() {
  const navigate = useNavigate()
  const preview = useQuery({
    queryKey: ['member-directory-preview'],
    queryFn: () =>
      getJSON<{
        member_directory_preview: { directory: { people: Array<{ name: string }>; total: number } }
      }>('/api/v1/member_directory_preview?rendering=members'),
    retry: false,
  })
  const directory = preview.data?.member_directory_preview.directory
  if (preview.isLoading)
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading directory preview...
      </p>
    )
  if (preview.isError || !directory) return <Alert>Directory preview unavailable</Alert>
  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-4 px-6 py-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin/directory/settings')}>
        <ArrowLeft className="mr-2 size-4" />
        Back to directory settings
      </Button>
      <h1 className="text-heading-sm font-semibold text-foreground">Member directory preview</h1>
      <p className="text-body-sm text-muted-foreground">
        Read-only preview · {directory.total} members
      </p>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        {directory.people.map((person) => (
          <div
            key={person.name}
            className="border-b border-border px-4 py-2.5 text-body-sm last:border-b-0"
          >
            {person.name}
          </div>
        ))}
        {directory.people.length === 0 && (
          <p className="px-4 py-3 text-body-sm text-muted-foreground">
            No members appear in this directory.
          </p>
        )}
      </div>
    </div>
  )
}
